/**
 * Dados mockados da página "Análise Cotação PME".
 *
 * Enquanto o backend do produto PME / da análise não está integrado, toda a
 * tela opera sobre estes dados fixos. Os identificadores das cotações vivem em
 * `services/quotes/pme-mock-analyses.ts`.
 */
import type { PmeLifeRow } from '@/features/proposals/components/pme/proposal.mock';

// ─── Cartão de Prêmio (cabeçalho) ───────────────────────────────────────────

export type PmeAnalysisPremium = {
  totalValue: string;
  netValue: string;
  iofValue: string;
  index: string;
  paymentFrequency: string;
  product: string;
  contractType: string;
  status: string;
};

export const PME_ANALYSIS_PREMIUM: PmeAnalysisPremium = {
  totalValue: 'R$ 2.530,00',
  netValue: 'R$ 2.530,00',
  iofValue: 'R$ 0,67',
  index: '1,2',
  paymentFrequency: 'Mensal',
  product: 'PME',
  contractType: 'Nova',
  status: 'Em Análise',
};

// ─── Aba "Critérios de Aceitação" ───────────────────────────────────────────

export type PmeCriterionStatus = 'OK' | 'Análise';

export type PmeAcceptanceCriterion = {
  /** Nome do critério (coluna "Critérios"). */
  name: string;
  /** Uma ou mais regras (coluna "Regras"). */
  rules: string[];
  /** Um resultado por regra (coluna "Resultado"). */
  results: string[];
  /** Veredito por regra: true = passou (fica cinza mesmo em critério "Análise");
   *  null/ausente = segue o status do critério. */
  ruleOk?: Array<boolean | null>;
  /** Status POR REGRA (coluna "Status"): uma tag por linha, alinhada com
   *  `rules`/`results`. Ausente → cai no status do critério. */
  lineStatus?: PmeCriterionStatus[];
  /** Status do critério — usado no fallback e na cor do nome do critério. */
  status: PmeCriterionStatus;
};

export const PME_ANALYSIS_CRITERIA: PmeAcceptanceCriterion[] = [
  {
    name: 'Capital Segurado',
    rules: [
      'Percentual de vidas com capital acima de R$ 200.000,00 deve ser menor ou igual a 20%',
    ],
    results: ['0,00%'],
    status: 'OK',
  },
  {
    name: 'Capital Vinculado ao Salário',
    rules: ['Deve ser menor ou igual a 4'],
    results: ['00'],
    status: 'OK',
  },
  {
    name: 'Coberturas Solicitadas',
    rules: ['Cobertura com subscrição requerida de ser igual a NÃO'],
    results: ['NÃO'],
    status: 'OK',
  },
  {
    name: 'Compartilho de Corretores',
    rules: [
      'Cotações em andamento, que possuam corretores diferentes, deve ser igual a NÃO',
    ],
    results: ['NÃO'],
    status: 'OK',
  },
  {
    name: 'Histórico de afastamento',
    rules: [
      'CID com restrição deve ser igual a NÃO',
      'Percentual de vidas afastadas deve ser menor ou igual a 3%',
      'Percentual de capital afastadas deve ser menor ou igual a 3%',
    ],
    results: ['NÃO', '0,00%', '0,00%'],
    status: 'Análise',
  },
  {
    name: 'Idade Máxima',
    rules: [
      'Quando SEGURO NOVO deve ser menor ou igual a 70 anos',
      'O montante de vidas acima de 70 anos deve ser menor ou igual a 3%',
    ],
    results: ['00', '0,00%'],
    status: 'OK',
  },
  {
    name: 'Idade Média',
    rules: ['Deve ser menor ou igual a 55 anos'],
    results: ['00'],
    status: 'OK',
  },
  {
    name: 'Natureza Jurídica',
    rules: ['Deve ser igual a PRIVADA'],
    results: ['PRIVADA'],
    status: 'OK',
  },
  {
    name: 'Nível de Risco CNAE',
    rules: ['Deve ser igual a BAIXO ou MÉDIO'],
    results: ['BAIXO'],
    status: 'OK',
  },
  {
    name: 'Quantidade de vidas',
    rules: ['Deve ser menor ou igual a 500 vidas'],
    results: ['000'],
    status: 'OK',
  },
];

// ─── Aba "Coberturas e Serviços" ────────────────────────────────────────────

export type PmeCoverageLine = {
  name: string;
  /** Detalhe sob o nome — nas diárias, "30 diárias × R$ 100,00". */
  detail?: string;
  totalInsuredCapital: string;
  averageRate: string;
  monthlyPremium: string;
};

export type PmeServiceLine = {
  name: string;
  valuePerInsured: string;
  monthlyValue: string;
};

export const PME_ANALYSIS_COVERAGES: PmeCoverageLine[] = [
  {
    name: 'Morte',
    totalInsuredCapital: 'R$ 00.000.000,00',
    averageRate: '0,000',
    monthlyPremium: 'R$ 0.000,00',
  },
  {
    name: 'Morte Acidental',
    totalInsuredCapital: 'R$ 00.000.000,00',
    averageRate: '0,000',
    monthlyPremium: 'R$ 0.000,00',
  },
];

export const PME_ANALYSIS_AVERAGE_MONTHLY_RATE = '0,000';

export const PME_ANALYSIS_SERVICES: PmeServiceLine[] = [
  {
    name: 'Assistência Funeral Familiar',
    valuePerInsured: 'R$ 0,00',
    monthlyValue: 'R$ 0,00',
  },
];

// ─── Aba "Idade / Capital" ──────────────────────────────────────────────────

export type PmeAgeCapitalRow = {
  age: string;
  capital: string;
  lives: string;
};

export const PME_ANALYSIS_AGE_CAPITAL: PmeAgeCapitalRow[] = Array.from(
  { length: 12 },
  () => ({
    age: '00',
    capital: 'R$ 00.000,00',
    lives: '0',
  })
);

// ─── Aba "Afastados" ────────────────────────────────────────────────────────

export type PmeRemovedLifeRow = {
  name: string;
  sex: string;
  age: string;
  capital: string;
  cids: string[];
};

export const PME_ANALYSIS_REMOVED_LIVES: PmeRemovedLifeRow[] = [
  {
    name: 'Nome Completo',
    sex: 'Masculino',
    age: '00',
    capital: 'R$ 00.000,00',
    cids: [],
  },
  {
    name: 'Nome Completo',
    sex: 'Feminino',
    age: '00',
    capital: 'R$ 00.000,00',
    cids: [],
  },
  {
    name: 'Nome Completo',
    sex: 'Masculino',
    age: '00',
    capital: 'R$ 00.000,00',
    cids: [],
  },
];

// ─── Seção "Versões" ────────────────────────────────────────────────────────

export type PmeAnalysisVersionRow = {
  type: string;
  date: string;
  number: string;
  lives: string;
  removed: string;
  averageRate: string;
  totalPremium: string;
};

export const PME_ANALYSIS_VERSIONS: PmeAnalysisVersionRow[] = [
  {
    type: 'Proposta',
    date: 'xx/xx/xxxx',
    number: 'Nº XXXX',
    lives: '000',
    removed: '0',
    averageRate: '0,0',
    totalPremium: 'R$ 000,00',
  },
  {
    type: 'Cotação',
    date: 'xx/xx/xxxx',
    number: 'Nº XXXX',
    lives: '000',
    removed: '0',
    averageRate: '0,0',
    totalPremium: 'R$ 000,00',
  },
];

// ─── Seção "Comercialização" ────────────────────────────────────────────────

export type PmeAnalysisCommercialization = {
  brokeragePercent: string;
  agencyPercent: string;
  surcharge: string;
  actuarialDiscount: string;
};

export const PME_ANALYSIS_COMMERCIALIZATION: PmeAnalysisCommercialization = {
  brokeragePercent: '45%',
  agencyPercent: '0%',
  surcharge: '0%',
  actuarialDiscount: '0%',
};

// ─── Seção "Dados da Cotação" → Lista de Estipulantes ───────────────────────

export const PME_ANALYSIS_STIPULANTS: string[] = [
  'Estipulante',
  'Subestipulante 1',
];

// ─── Lista de Vidas (mock) ──────────────────────────────────────────────────

export const PME_ANALYSIS_LIVES: PmeLifeRow[] = Array.from(
  { length: 6 },
  (_, index) => ({
    id: `life-${index + 1}`,
    status: 'active' as const,
    insurableGroup: 'Grupo Segurável',
    capital: 'R$ 00.000,00',
    sex: index % 2 === 0 ? 'M' : 'F',
    birthDate: 'XX/XX/XXXX',
    name: 'Nome Completo',
  })
);

export const PME_ANALYSIS_LIVES_TOTAL = 30;
